import { useParams, Navigate } from "react-router-dom";
import { FadeIn, MaskedLines, Overline, Chapter } from "@/components/Motion";
import CTABand from "@/components/CTABand";
import { IMAGES, PRODUCTS } from "@/data/content";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const TECH = {
  cdrive: {
    name: "CDRIVE™",
    overline: "Proprietary Drive Technology",
    lines: ["Brushless Performance.", "Absolute Silence."],
    sub: "A purpose-built brushless drivetrain engineered for one outcome: the quietest, smoothest, most reliable stairlift ride ever measured.",
    image: IMAGES.cdrive,
    dark: true,
    stats: [
      { value: "38", unit: "dB", label: "Measured noise level" },
      { value: "0", unit: "", label: "Brushes to replace" },
      { value: "50k", unit: "", label: "Cycle design life" },
      { value: "24V", unit: "", label: "DC brushless drive" },
    ],
    benefits: ["Ultra Silent", "Brushless Motor", "No Brushes", "Minimal Maintenance", "Long Life", "Smooth Ride", "Reduced Vibration", "Premium Comfort", "Energy Efficient", "Professional Reliability"],
    story: [
      { title: "No brushes. No wear.", text: "Traditional stairlift motors rely on carbon brushes that wear, spark and whine. CDRIVE™ eliminates them entirely — the result is silence measured at 38 dB and a drivetrain designed for 50,000 cycles." },
      { title: "Smooth by physics", text: "Field-oriented control delivers torque with zero cogging. Start, travel and stop happen as one continuous motion — no jolt, no hum, no vibration through the rail." },
      { title: "Efficiency as standard", text: "Brushless efficiency extends battery life, reduces charge cycles and cuts dealer service visits. Premium comfort for the user, premium economics for the distributor." },
    ],
    cta: { to: "/products", label: "See products powered by CDRIVE™" },
  },
  wsafe: {
    name: "WSAFE™",
    overline: "Outdoor Protection Technology",
    lines: ["Engineered for", "Every Season."],
    sub: "The complete outdoor protection system that makes the world's only outdoor curved stairlift possible — rain, UV, frost and salt air, answered.",
    image: IMAGES.wsafe,
    dark: false,
    stats: [
      { value: "IPX4", unit: "", label: "Sealed electronics" },
      { value: "−15°", unit: "", label: "Cold-rated operation" },
      { value: "+45°", unit: "", label: "Heat-rated operation" },
      { value: "365", unit: "", label: "Days of outdoor duty" },
    ],
    benefits: ["Outdoor Certified", "Weather Resistant", "Rain Protection", "UV Resistant", "Corrosion Resistant", "Outdoor Electronics", "Outdoor Components", "Reliable Performance", "Premium Outdoor Engineering"],
    story: [
      { title: "Sealed at every layer", text: "Conformal-coated circuit boards, gasketed housings and sealed connectors keep water and dust out of everything that matters — certified to IPX4 across the electronics chain." },
      { title: "Materials that outlast weather", text: "UV-stabilised polymers, anodised and powder-coated aluminium, and marine-grade fasteners resist sun, frost and coastal salt air year after year." },
      { title: "A category of one", text: "WSAFE™ enables ACTIVE Outdoor Curved — the world's only stairlift engineered for curved outdoor staircases. For distributors, it is a product no competitor can quote against." },
    ],
    cta: { to: "/products/active-outdoor-curved", label: "See ACTIVE Outdoor Curved" },
  },
};

export default function TechDetail() {
  const { slug } = useParams();
  const t = TECH[slug];
  if (!t) return <Navigate to="/technologies" replace />;

  const related = PRODUCTS.filter((p) =>
    slug === "cdrive" ? true : p.tech.includes("WSAFE™")
  );

  return (
    <>
      <section className={`${t.dark ? "bg-obsidian noise-dark" : "bg-white"} pt-36 sm:pt-44 pb-16`} data-testid="tech-hero">
        <div className={`relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8`}>
          <FadeIn>
            <Overline light={t.dark}>{t.overline}</Overline>
          </FadeIn>
          <h1 className={`mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.98] ${t.dark ? "text-white" : "text-slate-950"}`}>
            <span className="block">{t.name}</span>
            <MaskedLines immediate lines={t.lines} className={`${t.dark ? "text-slate-500" : "text-slate-400"} text-2xl sm:text-3xl lg:text-4xl font-medium mt-3`} />
          </h1>
          <FadeIn delay={0.3}>
            <p className={`mt-6 max-w-2xl text-base md:text-lg leading-relaxed ${t.dark ? "text-slate-400" : "text-slate-600"}`}>{t.sub}</p>
          </FadeIn>
          <FadeIn delay={0.35}>
            <div className={`spotlight-frame mt-12 rounded-2xl overflow-hidden border ${t.dark ? "border-white/10" : "border-slate-900/10"}`}>
              <img src={t.image} alt={t.name} className="w-full h-[320px] sm:h-[480px] object-cover" data-testid="tech-hero-image" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className={`${t.dark ? "bg-obsidian noise-dark border-t border-white/10" : "bg-[#F8F9FA] border-y border-slate-200/70"} py-20`} data-testid="tech-stats">
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {t.stats.map((s, i) => (
              <FadeIn key={s.label} delay={i * 0.08}>
                <div data-testid={`tech-stat-${i}`}>
                  <p className={`font-display text-4xl sm:text-5xl font-bold tracking-tighter ${t.dark ? "text-white" : "text-slate-950"}`}>
                    {s.value}<span className="text-signal text-2xl align-top ml-0.5">{s.unit}</span>
                  </p>
                  <p className={`mt-2 font-mono text-[10px] uppercase tracking-[0.2em] ${t.dark ? "text-slate-500" : "text-slate-500"}`}>{s.label}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className={`${t.dark ? "bg-obsidian noise-dark" : "bg-white"} py-24`} data-testid="tech-story">
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="01" label="Engineering Story" light={t.dark} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {t.story.map((s, i) => (
              <FadeIn key={s.title} delay={i * 0.08}>
                <div className={`h-full rounded-2xl border p-7 ${t.dark ? "border-white/10 bg-surface/60" : "border-slate-200/80 bg-[#F8F9FA]"}`}>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className={`mt-4 font-display text-lg font-semibold tracking-tight ${t.dark ? "text-white" : "text-slate-950"}`}>{s.title}</h3>
                  <p className={`mt-2 text-sm leading-relaxed ${t.dark ? "text-slate-400" : "text-slate-600"}`}>{s.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <div className="mt-10 flex flex-wrap gap-2">
              {t.benefits.map((b) => (
                <span key={b} className={`font-mono text-[10px] uppercase tracking-[0.18em] rounded-full px-3.5 py-1.5 border ${
                  t.dark ? "border-white/15 text-slate-300" : "border-slate-300 text-slate-600"
                }`}>
                  {b}
                </span>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      <section className={`${t.dark ? "bg-obsidian noise-dark border-t border-white/10" : "bg-[#F8F9FA] border-y border-slate-200/70"} py-24`} data-testid="tech-related">
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="02" label={`Powered by ${t.name}`} light={t.dark} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((p, i) => (
              <FadeIn key={p.slug} delay={i * 0.08}>
                <Link
                  to={`/products/${p.slug}`}
                  data-testid={`tech-related-${p.slug}`}
                  className={`group block rounded-2xl overflow-hidden border transition-all duration-300 hover:scale-[1.01] ${
                    t.dark ? "border-white/10 bg-surface/60 hover:border-signal/50" : "border-slate-200/80 bg-white hover:border-signal/40"
                  }`}
                >
                  <img src={p.image} alt={p.name} loading="lazy" className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="p-6">
                    <h3 className={`font-display text-lg font-semibold tracking-tight ${t.dark ? "text-white" : "text-slate-950"}`}>{p.name}</h3>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-medium text-signal">
                      View product <ArrowUpRight size={13} />
                    </span>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
